import React, { useImperativeHandle, useMemo, useState } from "react";
import { Nullable, WithErrors } from "../api/types";
import { FormHandle } from "../types";
import { getErrorMessage } from "../utils";
import useTestId from "./useTestId";
import _ from "lodash";

export default function useImperativeFormSubmit<T>({
    formValues,
    formData,
    ref,
    submitFunction,
    onSubmitSuccess,
    setErrors,
}: {
    formValues: Nullable<T>;
    formData: Nullable<T>;
    ref: React.ForwardedRef<FormHandle<Nullable<T>> | null>;
    submitFunction: (args: {
        testId: string;
        values: T;
    }) => Promise<Nullable<WithErrors<T>>>;
    onSubmitSuccess?: (data: T) => Promise<void>;
    setErrors?: (errors: Partial<Record<keyof T, string>>) => void;
}) {
    const [valuesErrors, setValuesErrors] =
        useState<Partial<Record<keyof T, string>>>();

    const [loading, setLoading] = useState(false);

    const testId = useTestId();

    const isDirty = useMemo(() => {
        if (!formValues || !formData) {
            return false;
        }
        for (const key of Object.keys(formValues)) {
            const _key = key as keyof T;
            const areEqual = _.isEqual(formValues[_key], formData[_key]);
            if (!areEqual) {
                return true;
            }
        }

        return false;
    }, [formValues, formData]);

    useImperativeHandle(
        ref,
        () => ({
            getFormData: () => formValues,
            onSubmit: async () => {
                let newTestId;
                if (!formValues || !testId) {
                    return { success: false };
                }
                setLoading(true);

                const newValues: Nullable<
                    (WithErrors<T> & { testId?: string }) | { error: string }
                > = await submitFunction({
                    testId,
                    values: formValues,
                });

                if (!newValues) {
                    setLoading(false);
                    return { success: false };
                }

                if (newValues.testId) {
                    newTestId = newValues.testId;
                }

                let hasErrors = false;
                const errors = {} as Partial<Record<keyof T, string>>;
                let mainErrorMessage: string | null = null;

                if ("error" in newValues) {
                    alert(newValues.error);
                    hasErrors = true;
                }

                for (const [key, value] of Object.entries(newValues)) {
                    let _errorMessage = getErrorMessage(value);
                    if (_errorMessage) {
                        setValuesErrors((prev) => ({
                            ...prev,
                            [key]: _errorMessage,
                        }));
                        hasErrors = true;
                        errors[key as keyof T] = _errorMessage;
                        mainErrorMessage = _errorMessage;
                    } else {
                        setValuesErrors((prev) => ({
                            ...prev,
                            [key]: undefined,
                        }));
                    }
                }
                setLoading(false);
                if (!hasErrors) await onSubmitSuccess?.(newValues as T);
                if (!_.isEmpty(errors)) {
                    setErrors?.(errors);
                }
                return {
                    success: !hasErrors,
                    testId: newTestId,
                    isDirty,
                    errorMessage: mainErrorMessage,
                };
            },
            isDirty,
        }),
        [formValues, submitFunction, testId, isDirty]
    );

    return { valuesErrors, submissionLoading: loading };
}
